import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionActionTypes from "../extractions/actionTypes";
import * as extVersionActionTypes from "../extVersions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { viewFullData } from "../extractions/action";
import constants from "../../commonUtils/constants";

function* getOutputMappingsSaga({ payload }) {
    try {
        const { id, key, navigate=null } = payload;
        
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_OUTPUT_MAPPINGS_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/output/${id}/mappings`);
        
        if (data.success) {
            yield put({ 
                type: actionTypes.GET_OUTPUT_MAPPINGS_SUCCESS, 
                payload: data.data 
            });
            yield put({ type: extractionActionTypes.SET_VIEW_MODE, payload: !data?.data?.isCreator });
            if(navigate) navigate(`/extraction/view/${key}`)
        } else {
            toast.error(data?.message || "Failed to fetch output mappings.");
            yield put({ 
                type: actionTypes.GET_OUTPUT_MAPPINGS_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Failed to fetch output mappings.");
        const data = error?.response?.data;
        yield put({ 
            type: actionTypes.GET_OUTPUT_MAPPINGS_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
}

function* reprocessOutputMappingsSaga({ versionId }) {
    try {
        yield put({ type: actionTypes.SET_REPROCESSING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.REPROCESS_OUTPUT_MAPPINGS_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/output/${versionId}/reprocess-mappings`);
        
        if (data.success) {
            toast.success("Output mappings reprocessed successfully!");
            yield put({ 
                type: actionTypes.REPROCESS_OUTPUT_MAPPINGS_SUCCESS, 
                payload: data.data 
            });
            yield put({ 
                type: actionTypes.GET_OUTPUT_MAPPINGS, 
                versionId 
            });
        } else {
            toast.error(data?.message || "Failed to reprocess output mappings.");
            yield put({ 
                type: actionTypes.REPROCESS_OUTPUT_MAPPINGS_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;

        if (status === 409) {
            toast.info(data?.message || "Output mappings already exist for this version.");
            yield put({
                type: actionTypes.REPROCESS_OUTPUT_MAPPINGS_SUCCESS,
                payload: { noOp: true, ...(data?.data || {}) }
            });
            return;
        }

        handleApiError(error, "Failed to reprocess output mappings.");
        yield put({ 
            type: actionTypes.REPROCESS_OUTPUT_MAPPINGS_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
}

function* getSingleOutputMappingSaga({ payload }) {
    try {
        const { versionKey, dataGroupTitle, dataCategoryTitle, currentPage, perPage } = payload;
        
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.get(
            `/ext/output/${versionKey}/mappings/single`,
            {
                params: {
                    dataGroupTitle,
                    dataCategoryTitle
                }
            }
        );
        
        if (data.success) {
            const respData = data?.data;

            const extOutputMappingId = respData?._id;
            const extraction         = respData?.extraction;
            const extVersion         = respData?.ext_version;
            const extDataGroup       = respData?.data_group;
            const extDataCategory    = respData?.data_category;

            if (!extOutputMappingId) {
                yield put({
                    type: actionTypes.GET_SINGLE_OUTPUT_MAPPING_FAILURE,
                    payload: { message: 'Output mapping _id missing from response.' }
                });
                return;
            }

            yield put({
                type: actionTypes.GET_SINGLE_OUTPUT_MAPPING_SUCCESS,
                payload: {
                    _id:          extOutputMappingId,
                    dataGroup:    extDataGroup,
                    dataCategory: extDataCategory,
                },
            });

            yield put({ type: extractionActionTypes.SET_EXTRACTION_DATA, payload: extraction });
            yield put({ type: extractionActionTypes.SET_STEP_VAL, payload: 4 });
            yield put({ type: extVersionActionTypes.SET_EXT_VERSION, payload: extVersion });

            // Pass extOutputMappingId as the single source of truth —
            // the backend resolves extVersion/dataGroup/dataCategory from it directly
            yield put(viewFullData({
                extOutputMappingId,
                currentPage: currentPage || 1,
                perPage:     perPage || constants.extraction.fullDataListInitialState.pagination.perPage
            }));

        } else {
            toast.error(data?.message || "Failed to fetch output mapping.");
            yield put({
                type: actionTypes.GET_SINGLE_OUTPUT_MAPPING_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Failed to fetch output mapping.");
        const data = error?.response?.data;
        yield put({ 
            type: actionTypes.GET_SINGLE_OUTPUT_MAPPING_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
}

function* ExtOutputMappingsSaga() {
    yield all([
        takeLatest(actionTypes.GET_OUTPUT_MAPPINGS, getOutputMappingsSaga),
        takeLatest(actionTypes.REPROCESS_OUTPUT_MAPPINGS, reprocessOutputMappingsSaga),
        takeLatest(actionTypes.GET_SINGLE_OUTPUT_MAPPING, getSingleOutputMappingSaga),
    ]);
}

export default ExtOutputMappingsSaga;